import React, { useState, useMemo } from "react";
import { Plus, X, Edit2, Trash2, RotateCcw, Ruler, Upload, Download, Send, PackageCheck, Wrench, History } from "lucide-react";
import { toast } from "sonner";
import { PageHead } from "../components/PageHead";
import { DataTable } from "../components/DataTable";
import { Modal } from "../components/Modal";
import { SubcontractorTypeahead } from "../components/SubcontractorTypeahead";
import { useAuth } from "../context/AuthContext";
import { fmtDate, fmtDateTime, fmtCalPeriod } from "../lib/format";
import api, { apiErr } from "../lib/api";

// Known areas from the client's current register — free text, these are
// just convenience suggestions (datalist), not an enforced enum.
const AREAS = ["Dimensional", "Pressure", "Torque", "Temperature", "Electronic", "Force", "Mass", "WorkShop"];
const PERIOD_PRESETS = [365, 547, 730, 1095, 1460, 1825, 2190];

const EMPTY = {
  kind: "STD", area: "", unit_code: "", description: "", manufacturer: "", model_type: "",
  serial_number: "", certificate_number: "", calibration_date: "", calibration_period_days: "",
  remark: "",
};

function daysUntil(dueDate) {
  if (!dueDate) return null;
  const ms = new Date(dueDate).setHours(0, 0, 0, 0) - new Date().setHours(0, 0, 0, 0);
  return Math.round(ms / 86400000);
}

export function DueBadge({ dueDate }) {
  if (!dueDate) return <span className="t-mut">—</span>;
  const d = daysUntil(dueDate);
  let cls = "b-green";
  if (d < 0) cls = "b-red"; else if (d <= 90) cls = "b-amber";
  return (
    <span className={`badge ${cls}`} data-testid="standards-due-badge">
      <span className="bd" />{fmtDate(dueDate)} {d < 0 ? `(${Math.abs(d)}d overdue)` : `(${d}d)`}
    </span>
  );
}

function LocationBadge({ r }) {
  if (r.location_status === "sent_to_subcontractor") {
    return <span className="badge b-blue"><span className="bd" />Sent — {r.subcontractor_name || "subcontractor"}</span>;
  }
  return <span className="badge"><span className="bd" />In-house</span>;
}

function CertificatesPanel({ std, onChanged }) {
  const [certNo, setCertNo] = useState("");
  const [certDate, setCertDate] = useState("");
  const [source, setSource] = useState("in_house");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const download = async (a) => {
    try {
      const r = await api.get(`/standards/${std.id}/attachments/${a.id}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const link = document.createElement("a");
      link.href = url; link.download = a.filename || "certificate";
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const upload = async () => {
    if (!file) return toast.error("Choose a file first");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (certNo) fd.append("certificate_number", certNo);
      if (certDate) fd.append("certificate_date", certDate);
      fd.append("source", source);
      await api.post(`/standards/${std.id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Certificate uploaded");
      setCertNo(""); setCertDate(""); setFile(null);
      onChanged();
    } catch (e) { toast.error(apiErr(e)); } finally { setUploading(false); }
  };

  return (
    <div style={{ display: "grid", gap: 12 }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, alignItems: "end" }}>
        <div className="fld">
          <label>Certificate #</label>
          <input className="input" value={certNo} onChange={(e) => setCertNo(e.target.value)}
                 data-testid="std-cert-number-input" />
        </div>
        <div className="fld">
          <label>Certificate date</label>
          <input className="input" type="date" value={certDate} onChange={(e) => setCertDate(e.target.value)}
                 data-testid="std-cert-date-input" />
        </div>
        <div className="fld">
          <label>Source</label>
          <select className="input" value={source} onChange={(e) => setSource(e.target.value)} data-testid="std-cert-source-select">
            <option value="in_house">PTE's own master</option>
            <option value="subcontractor">Received from subcontractor</option>
          </select>
        </div>
      </div>
      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
        <input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="std-cert-file-input" />
        <button className="btn gold" onClick={upload} disabled={uploading} data-testid="std-cert-upload-btn">
          <Upload size={13} /> {uploading ? "Uploading…" : "Upload"}
        </button>
      </div>
      <div className="tbl-wrap">
        <table>
          <thead><tr><th>File</th><th>Cert #</th><th>Date</th><th>Source</th><th>Uploaded</th><th></th></tr></thead>
          <tbody>
            {(std.attachments || []).filter((a) => !a.is_deleted).length === 0 && (
              <tr><td colSpan={6} className="t-mut" style={{ textAlign: "center", padding: 14 }}>No certificates uploaded yet.</td></tr>
            )}
            {(std.attachments || []).filter((a) => !a.is_deleted).map((a) => (
              <tr key={a.id}>
                <td>{a.filename}</td>
                <td className="mono">{a.certificate_number || "—"}</td>
                <td className="mono t-mut">{a.certificate_date ? fmtDate(a.certificate_date) : "—"}</td>
                <td>{a.source === "subcontractor" ? "Subcontractor" : "PTE master"}</td>
                <td className="t-mut" style={{ fontSize: 11.5 }}>{fmtDateTime(a.uploaded_at)} · {a.uploaded_by_name}</td>
                <td>
                  <button className="btn ghost sm" onClick={() => download(a)}
                          data-testid={`std-cert-download-${a.id}`}>
                    <Download size={12} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StandardModal({ open, initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial || EMPTY);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("details");
  const [calDate, setCalDate] = useState(new Date().toISOString().slice(0, 10));
  const [calPeriod, setCalPeriod] = useState(initial?.calibration_period_days || 365);
  const [calCert, setCalCert] = useState("");
  const [subconName, setSubconName] = useState("");
  const [expectedReturn, setExpectedReturn] = useState("");
  const [returnDate, setReturnDate] = useState(new Date().toISOString().slice(0, 10));
  const [returnCalDate, setReturnCalDate] = useState("");
  const [returnCert, setReturnCert] = useState("");

  React.useEffect(() => {
    if (open) { setForm(initial || EMPTY); setTab("details"); setCalPeriod(initial?.calibration_period_days || 365); }
  }, [open, initial]);
  if (!open) return null;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!form.description?.trim()) return toast.error("Description is required.");
    setBusy(true);
    try {
      const payload = { ...form, calibration_period_days: form.calibration_period_days ? Number(form.calibration_period_days) : null };
      if (initial?.id) {
        await api.patch(`/standards/${initial.id}`, payload);
        toast.success(`Updated "${form.description}"`);
      } else {
        const r = await api.post("/standards", payload);
        toast.success(`Added "${form.description}"`);
        setForm(r.data);
      }
      onSaved();
      if (!initial?.id) onClose();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const doCalibrate = async () => {
    if (!calDate) return toast.error("Calibration date is required");
    setBusy(true);
    try {
      const r = await api.post(`/standards/${initial.id}/calibrate`, {
        calibration_date: calDate, calibration_period_days: Number(calPeriod), certificate_number: calCert || undefined,
      });
      toast.success("In-house calibration recorded — due date updated");
      setForm(r.data); onSaved();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const doSendOut = async () => {
    if (!subconName.trim()) return toast.error("Subcontractor name is required");
    setBusy(true);
    try {
      const r = await api.post(`/standards/${initial.id}/send-to-subcontractor`, {
        subcontractor_name: subconName, expected_return_date: expectedReturn || undefined,
      });
      toast.success(`Sent to ${subconName}`);
      setForm(r.data); setSubconName(""); setExpectedReturn(""); onSaved();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const doReturn = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/standards/${initial.id}/return-from-subcontractor`, {
        returned_date: returnDate, calibration_date: returnCalDate || undefined, certificate_number: returnCert || undefined,
      });
      toast.success("Marked as returned");
      setForm(r.data); setReturnCalDate(""); setReturnCert(""); onSaved();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <Modal open={open} onClose={onClose} icon={Ruler} wide
           title={initial?.id ? form.description : "Add PTE Standard / Equipment"}
           subtitle={initial?.id ? `${form.area || "—"} · ${form.serial_number || "no serial"}` : "Register a new reference standard or lab support equipment"}>
      {initial?.id && (
        <div className="ftabs" style={{ marginBottom: 12 }}>
          <button className={`ftab ${tab === "details" ? "active" : ""}`} onClick={() => setTab("details")} data-testid="std-tab-details">Details</button>
          <button className={`ftab ${tab === "actions" ? "active" : ""}`} onClick={() => setTab("actions")} data-testid="std-tab-actions">
            <Wrench size={12} style={{ verticalAlign: "-2px", marginRight: 4 }} />Calibrate / Subcontract
          </button>
          <button className={`ftab ${tab === "certs" ? "active" : ""}`} onClick={() => setTab("certs")} data-testid="std-tab-certs">Certificates ({(form.attachments || []).length})</button>
          <button className={`ftab ${tab === "history" ? "active" : ""}`} onClick={() => setTab("history")} data-testid="std-tab-history"><History size={12} style={{ verticalAlign: "-2px" }} /></button>
        </div>
      )}

      {tab === "details" && (
        <div style={{ display: "grid", gap: 12 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
            <div className="fld">
              <label>Type</label>
              <select className="input" value={form.kind || "STD"} onChange={set("kind")} data-testid="std-field-kind">
                <option value="STD">Standard</option>
                <option value="Eqp">Supporting equipment</option>
              </select>
            </div>
            <div className="fld">
              <label>Area</label>
              <input className="input" list="std-areas" value={form.area || ""} onChange={set("area")} data-testid="std-field-area" />
              <datalist id="std-areas">{AREAS.map((a) => <option key={a} value={a} />)}</datalist>
            </div>
            <div className="fld">
              <label>Unit / lab code</label>
              <input className="input" value={form.unit_code || ""} onChange={set("unit_code")} data-testid="std-field-unit-code" />
            </div>
          </div>
          <div className="fld">
            <label>Description<span style={{ color: "var(--red)" }}>*</span></label>
            <input className="input" value={form.description || ""} onChange={set("description")} data-testid="std-field-description" />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div className="fld">
              <label>Manufacturer</label>
              <input className="input" value={form.manufacturer || ""} onChange={set("manufacturer")} data-testid="std-field-manufacturer" />
            </div>
            <div className="fld">
              <label>Model / Type</label>
              <input className="input" value={form.model_type || ""} onChange={set("model_type")} data-testid="std-field-model" />
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div className="fld">
              <label>Serial number</label>
              <input className="input" value={form.serial_number || ""} onChange={set("serial_number")} data-testid="std-field-serial" />
            </div>
            <div className="fld">
              <label>Current certificate #</label>
              <input className="input mono" value={form.certificate_number || ""} onChange={set("certificate_number")} data-testid="std-field-cert-no" />
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div className="fld">
              <label>Last calibration date</label>
              <input className="input" type="date" value={form.calibration_date || ""} onChange={set("calibration_date")} data-testid="std-field-cal-date" />
            </div>
            <div className="fld">
              <label>Calibration period (days)</label>
              <input className="input" type="number" value={form.calibration_period_days || ""} onChange={set("calibration_period_days")}
                     data-testid="std-field-period" />
              {form.calibration_period_days ? <div className="t-mut" style={{ fontSize: 11.5, marginTop: 3 }}>{fmtCalPeriod(Number(form.calibration_period_days)) || ""}</div> : null}
            </div>
          </div>
          <div className="fld">
            <label>Remark</label>
            <textarea className="input" rows={2} value={form.remark || ""} onChange={set("remark")} data-testid="std-field-remark" />
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <button className="btn gold" onClick={save} disabled={busy} data-testid="std-save-btn">{busy ? "Saving…" : "Save"}</button>
          </div>
        </div>
      )}

      {tab === "actions" && initial?.id && (
        <div style={{ display: "grid", gap: 18 }}>
          <div className="card pad">
            <div className="sec-h"><h3 style={{ fontSize: 14 }}><Wrench size={14} style={{ verticalAlign: "-2px", marginRight: 6 }} />Record in-house calibration</h3></div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginTop: 8 }}>
              <div className="fld"><label>Calibration date</label>
                <input className="input" type="date" value={calDate} onChange={(e) => setCalDate(e.target.value)} data-testid="std-calibrate-date" /></div>
              <div className="fld"><label>Period (days)</label>
                <input className="input" type="number" list="std-periods" value={calPeriod} onChange={(e) => setCalPeriod(e.target.value)} data-testid="std-calibrate-period" />
                <datalist id="std-periods">{PERIOD_PRESETS.map((p) => <option key={p} value={p}>{fmtCalPeriod(p)}</option>)}</datalist>
              </div>
              <div className="fld"><label>Certificate # (optional)</label>
                <input className="input mono" value={calCert} onChange={(e) => setCalCert(e.target.value)} data-testid="std-calibrate-cert" /></div>
            </div>
            <button className="btn gold" style={{ marginTop: 10 }} onClick={doCalibrate} disabled={busy} data-testid="std-calibrate-btn">
              <Wrench size={13} /> Record calibration
            </button>
          </div>

          {form.location_status !== "sent_to_subcontractor" ? (
            <div className="card pad">
              <div className="sec-h"><h3 style={{ fontSize: 14 }}><Send size={14} style={{ verticalAlign: "-2px", marginRight: 6 }} />Send to subcontractor (SANAS lab)</h3></div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 8 }}>
                <div className="fld"><label>Subcontractor</label>
                  <SubcontractorTypeahead value={subconName} onChange={setSubconName} testid="std-subcon-name"
                                          placeholder="Start typing a subcontractor name…" /></div>
                <div className="fld"><label>Expected return date</label>
                  <input className="input" type="date" value={expectedReturn} onChange={(e) => setExpectedReturn(e.target.value)} data-testid="std-sent-expected-return" /></div>
              </div>
              <button className="btn" style={{ marginTop: 10 }} onClick={doSendOut} disabled={busy} data-testid="std-send-out-btn">
                <Send size={13} /> Send to subcontractor
              </button>
            </div>
          ) : (
            <div className="card pad">
              <div className="sec-h"><h3 style={{ fontSize: 14 }}><PackageCheck size={14} style={{ verticalAlign: "-2px", marginRight: 6 }} />Mark returned from {form.subcontractor_name || "subcontractor"}</h3></div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginTop: 8 }}>
                <div className="fld"><label>Returned date</label>
                  <input className="input" type="date" value={returnDate} onChange={(e) => setReturnDate(e.target.value)} data-testid="std-return-date" /></div>
                <div className="fld"><label>New calibration date (if recalibrated)</label>
                  <input className="input" type="date" value={returnCalDate} onChange={(e) => setReturnCalDate(e.target.value)} data-testid="std-return-cal-date" /></div>
                <div className="fld"><label>Certificate # received</label>
                  <input className="input mono" value={returnCert} onChange={(e) => setReturnCert(e.target.value)} data-testid="std-return-cert" /></div>
              </div>
              <button className="btn gold" style={{ marginTop: 10 }} onClick={doReturn} disabled={busy} data-testid="std-return-btn">
                <PackageCheck size={13} /> Mark returned
              </button>
            </div>
          )}
        </div>
      )}

      {tab === "certs" && initial?.id && <CertificatesPanel std={form} onChanged={async () => {
        const r = await api.get(`/standards/${initial.id}`); setForm(r.data); onSaved();
      }} />}

      {tab === "history" && initial?.id && (
        <div className="tbl-wrap">
          <table>
            <thead><tr><th>When</th><th>Action</th><th>By</th><th>Note</th></tr></thead>
            <tbody>
              {(form.history || []).slice().reverse().map((h, i) => (
                <tr key={i}>
                  <td className="mono t-mut">{fmtDateTime(h.at)}</td>
                  <td>{(h.action || "").replace(/_/g, " ")}</td>
                  <td>{h.by_name || "—"}</td>
                  <td className="t-mut">{h.note || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}

export default function Standards() {
  const { can } = useAuth();
  const canEdit = can("standards", "edit");
  const [refresh, setRefresh] = useState(0);
  const [modal, setModal] = useState(null);
  const [tabKey, setTabKey] = useState("all");

  const onDelete = async (row) => {
    if (!window.confirm(`Remove "${row.description}" from the PTE Standards register?\n\nIt will be hidden but can be restored.`)) return;
    try {
      await api.delete(`/standards/${row.id}`);
      toast.success(`Removed "${row.description}"`);
      setRefresh((x) => x + 1);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const columns = [
    { key: "kind", label: "Type", sortable: true, render: (v) => v === "STD" ? <span className="badge b-violet"><span className="bd" />STD</span> : <span className="badge"><span className="bd" />Eqp</span> },
    { key: "area", label: "Area", sortable: true, colSearch: true, render: (v) => v || <span className="t-mut">—</span> },
    { key: "description", label: "Description", sortable: true, colSearch: true,
      render: (v, r) => <span className="t-strong">{v}</span> },
    { key: "manufacturer", label: "Manufacturer", colSearch: true, render: (v) => v || <span className="t-mut">—</span> },
    { key: "serial_number", label: "Serial #", colSearch: true, mono: true, render: (v) => v || <span className="t-mut">—</span> },
    { key: "certificate_number", label: "Cert #", colSearch: true, mono: true, render: (v) => v || <span className="t-mut">—</span> },
    { key: "calibration_due_date", label: "Due", sortable: true, render: (v) => <DueBadge dueDate={v} /> },
    { key: "location_status", label: "Status", render: (v, r) => <LocationBadge r={r} /> },
    canEdit && {
      key: "_actions", label: "", sortable: false,
      render: (_, r) => (
        <span style={{ display: "inline-flex", gap: 4 }}>
          <button className="btn ghost sm" onClick={(e) => { e.stopPropagation(); setModal({ initial: r }); }}
                  data-testid={`std-edit-${r.id}`} title="Edit / Calibrate / Subcontract">
            <Edit2 size={12} />
          </button>
          <button className="btn ghost sm" onClick={(e) => { e.stopPropagation(); onDelete(r); }}
                  data-testid={`std-del-${r.id}`} title="Remove">
            <Trash2 size={12} color="var(--red)" />
          </button>
        </span>
      ),
    },
  ].filter(Boolean);

  const today = new Date().toISOString().slice(0, 10);
  const filteredParams = useMemo(() => ({}), []);

  return (
    <>
      <PageHead crumb="Records" title="PTE Standards"
        sub="PTE's own reference standards and supporting lab equipment — separate from the customer Equipment Register. Tracks in-house vs sent-to-subcontractor status, uploaded certificates, and gives a 3-month heads-up before anything lapses." />
      <DataTable endpoint="/standards" params={filteredParams} columns={columns} rowKey="id" testid="standards"
        defaultSort={{ by: "calibration_due_date", dir: "asc" }} clientSide initialLimit={500}
        searchPlaceholder="Search description, manufacturer, serial, certificate…"
        refreshSignal={refresh}
        onRowClick={(row) => setModal({ initial: row })}
        filterRows={(rows) => {
          if (tabKey === "due_soon") return rows.filter((r) => r.calibration_due_date && r.calibration_due_date >= today && daysUntil(r.calibration_due_date) <= 90);
          if (tabKey === "overdue") return rows.filter((r) => r.calibration_due_date && r.calibration_due_date < today);
          if (tabKey === "subcontracted") return rows.filter((r) => r.location_status === "sent_to_subcontractor");
          return rows;
        }}
        toolbar={
          <>
            <button className={`ftab ${tabKey === "all" ? "active" : ""}`} onClick={() => setTabKey("all")} data-testid="std-tab-all">All</button>
            <button className={`ftab ${tabKey === "due_soon" ? "active" : ""}`} onClick={() => setTabKey("due_soon")} data-testid="std-tab-due-soon">Due Soon (≤90 days)</button>
            <button className={`ftab ${tabKey === "overdue" ? "active" : ""}`} onClick={() => setTabKey("overdue")} data-testid="std-tab-overdue">Overdue</button>
            <button className={`ftab ${tabKey === "subcontracted" ? "active" : ""}`} onClick={() => setTabKey("subcontracted")} data-testid="std-tab-subcontracted">Sent to Subcontractor</button>
          </>
        }
        rightToolbar={canEdit && (
          <button className="btn gold" onClick={() => setModal({ initial: null })} data-testid="std-add-btn">
            <Plus size={13} /> Add standard
          </button>
        )}
      />
      <StandardModal open={!!modal} initial={modal?.initial} onClose={() => setModal(null)} onSaved={() => setRefresh((x) => x + 1)} />
    </>
  );
}
